import { useState, useEffect } from 'react';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/App';
import { Car, DollarSign, Calendar, Plus, Edit, Trash2 } from 'lucide-react';

const AdminPage = () => {
  const [stats, setStats] = useState({ total_cars: 0, total_bookings: 0, total_revenue: 0 });
  const [cars, setCars] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddCarOpen, setIsAddCarOpen] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [carFormData, setCarFormData] = useState({
    name: '',
    brand: '',
    type: 'SUV',
    price_per_day: '',
    location: '',
    image_url: '',
    description: '',
    features: '',
    seats: '',
    transmission: 'Automatic',
    fuel_type: 'Petrol'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, carsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/cars`),
        axios.get(`${API}/bookings/all`)
      ]);
      
      setStats(statsRes.data);
      setCars(carsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCar = async (e) => {
    e.preventDefault();
    
    const carData = {
      ...carFormData,
      price_per_day: parseFloat(carFormData.price_per_day),
      seats: parseInt(carFormData.seats),
      features: carFormData.features.split(',').map(f => f.trim())
    };

    try {
      if (editingCar) {
        await axios.put(`${API}/cars/${editingCar.id}`, carData);
        toast.success('Car updated successfully');
      } else {
        await axios.post(`${API}/cars`, carData);
        toast.success('Car added successfully');
      }
      
      setIsAddCarOpen(false);
      setEditingCar(null);
      resetCarForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save car');
    }
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm('Are you sure you want to delete this car?')) return;
    
    try {
      await axios.delete(`${API}/cars/${carId}`);
      toast.success('Car deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete car');
    }
  };

  const handleEditCar = (car) => {
    setEditingCar(car);
    setCarFormData({
      name: car.name,
      brand: car.brand,
      type: car.type,
      price_per_day: car.price_per_day.toString(),
      location: car.location,
      image_url: car.image_url,
      description: car.description,
      features: car.features.join(', '),
      seats: car.seats.toString(),
      transmission: car.transmission,
      fuel_type: car.fuel_type
    });
    setIsAddCarOpen(true);
  };

  const resetCarForm = () => {
    setCarFormData({
      name: '',
      brand: '',
      type: 'SUV',
      price_per_day: '',
      location: '',
      image_url: '',
      description: '',
      features: '',
      seats: '',
      transmission: 'Automatic',
      fuel_type: 'Petrol'
    });
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Loading admin panel...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="admin-title">
            Admin Dashboard
          </h1>
          <p className="text-lg text-muted-foreground">Manage cars and bookings</p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="glass-card p-6 rounded-lg" data-testid="admin-stat-cars">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Cars</p>
                <p className="text-3xl font-bold">{stats.total_cars}</p>
              </div>
              <Car className="w-10 h-10 text-accent" />
            </div>
          </div>

          <div className="glass-card p-6 rounded-lg" data-testid="admin-stat-bookings">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Bookings</p>
                <p className="text-3xl font-bold">{stats.total_bookings}</p>
              </div>
              <Calendar className="w-10 h-10 text-accent" />
            </div>
          </div>

          <div className="glass-card p-6 rounded-lg" data-testid="admin-stat-revenue">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Revenue</p>
                <p className="text-3xl font-bold">Rs. {stats.total_revenue.toLocaleString()}</p>
              </div>
              <DollarSign className="w-10 h-10 text-accent" />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="cars" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="cars" data-testid="admin-tab-cars">Cars</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
          </TabsList>

          {/* Cars Tab */}
          <TabsContent value="cars">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Cabinet Grotesk' }}>Manage Cars</h2>
              <Dialog open={isAddCarOpen} onOpenChange={setIsAddCarOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" onClick={() => { setEditingCar(null); resetCarForm(); }} data-testid="admin-add-car-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Car
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingCar ? 'Edit Car' : 'Add New Car'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitCar} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Car Name</Label>
                        <Input
                          id="name"
                          value={carFormData.name}
                          onChange={(e) => setCarFormData({ ...carFormData, name: e.target.value })}
                          required
                          data-testid="car-form-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="brand">Brand</Label>
                        <Input
                          id="brand"
                          value={carFormData.brand}
                          onChange={(e) => setCarFormData({ ...carFormData, brand: e.target.value })}
                          required
                          data-testid="car-form-brand"
                        />
                      </div>
                      <div>
                        <Label htmlFor="type">Type</Label>
                        <Select value={carFormData.type} onValueChange={(value) => setCarFormData({ ...carFormData, type: value })}>
                          <SelectTrigger data-testid="car-form-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="SUV">SUV</SelectItem>
                            <SelectItem value="Sedan">Sedan</SelectItem>
                            <SelectItem value="Sports">Sports</SelectItem>
                            <SelectItem value="Luxury">Luxury</SelectItem>
                            <SelectItem value="Compact">Compact</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="price">Price per Day (Rs.)</Label>
                        <Input
                          id="price"
                          type="number"
                          step="0.01"
                          value={carFormData.price_per_day}
                          onChange={(e) => setCarFormData({ ...carFormData, price_per_day: e.target.value })}
                          required
                          data-testid="car-form-price"
                        />
                      </div>
                      <div>
                        <Label htmlFor="location">Location</Label>
                        <Input
                          id="location"
                          value={carFormData.location}
                          onChange={(e) => setCarFormData({ ...carFormData, location: e.target.value })}
                          required
                          data-testid="car-form-location"
                        />
                      </div>
                      <div>
                        <Label htmlFor="seats">Seats</Label>
                        <Input
                          id="seats"
                          type="number"
                          value={carFormData.seats}
                          onChange={(e) => setCarFormData({ ...carFormData, seats: e.target.value })}
                          required
                          data-testid="car-form-seats"
                        />
                      </div>
                      <div>
                        <Label htmlFor="transmission">Transmission</Label>
                        <Select value={carFormData.transmission} onValueChange={(value) => setCarFormData({ ...carFormData, transmission: value })}>
                          <SelectTrigger data-testid="car-form-transmission">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Automatic">Automatic</SelectItem>
                            <SelectItem value="Manual">Manual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="fuel_type">Fuel Type</Label>
                        <Select value={carFormData.fuel_type} onValueChange={(value) => setCarFormData({ ...carFormData, fuel_type: value })}>
                          <SelectTrigger data-testid="car-form-fuel">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Petrol">Petrol</SelectItem>
                            <SelectItem value="Diesel">Diesel</SelectItem>
                            <SelectItem value="Electric">Electric</SelectItem>
                            <SelectItem value="Hybrid">Hybrid</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="image_url">Image URL</Label>
                      <Input
                        id="image_url"
                        value={carFormData.image_url}
                        onChange={(e) => setCarFormData({ ...carFormData, image_url: e.target.value })}
                        required
                        data-testid="car-form-image"
                      />
                    </div>
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Input
                        id="description"
                        value={carFormData.description}
                        onChange={(e) => setCarFormData({ ...carFormData, description: e.target.value })}
                        required
                        data-testid="car-form-description"
                      />
                    </div>
                    <div>
                      <Label htmlFor="features">Features (comma-separated)</Label>
                      <Input
                        id="features"
                        placeholder="GPS, Bluetooth, AC"
                        value={carFormData.features}
                        onChange={(e) => setCarFormData({ ...carFormData, features: e.target.value })}
                        required
                        data-testid="car-form-features"
                      />
                    </div>
                    <Button type="submit" className="w-full btn-primary" data-testid="car-form-submit">
                      {editingCar ? 'Update Car' : 'Add Car'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.map((car, index) => (
                <div key={car.id} className="glass-card rounded-lg overflow-hidden" data-testid={`admin-car-card-${index}`}>
                  <div className="aspect-video overflow-hidden">
                    <img src={car.image_url} alt={car.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-2">{car.name}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{car.type} - {car.location}</p>
                    <p className="text-xl font-bold text-primary mb-4">Rs. {car.price_per_day.toLocaleString()}/day</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditCar(car)} className="flex-1" data-testid={`admin-edit-car-${index}`}>
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteCar(car.id)} className="flex-1" data-testid={`admin-delete-car-${index}`}>
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>All Bookings</h2>
            <div className="space-y-4">
              {bookings.map((booking, index) => (
                <div key={booking.id} className="glass-card p-6 rounded-lg" data-testid={`admin-booking-card-${index}`}>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Booking ID</p>
                      <p className="font-mono text-sm">{booking.id.substring(0, 8)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Dates</p>
                      <p className="text-sm">{new Date(booking.start_date).toLocaleDateString()} - {new Date(booking.end_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total</p>
                      <p className="font-bold">Rs. {booking.total_price.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <div className="flex gap-2">
                        <span className={`px-2 py-1 rounded text-xs ${booking.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {booking.status}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs ${booking.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {booking.payment_status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default AdminPage;
